import React, { useState, useEffect, useCallback } from "react";
import {
  EditButton,
  List,
  ShowButton,
} from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  IResourceComponentsProps,
  useTranslate,
  useNotification,
} from "@refinedev/core";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Avatar,
  Stack,
  LinearProgress,
  Paper,
  Alert,
  Tooltip,
  useMediaQuery,
  Tab,
  Tabs,
  alpha,
  InputAdornment,
  Autocomplete,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import {
  PersonAdd,
  Upload,
  Search,
  CheckCircle,
  Cancel,
  ContentCopy,
  Group,
  GroupAdd,
  Email,
  Phone,
  Download,
  Close,
  Send,
  AccountTree,
  FilterList,
  Clear,
  ViewList,
  ViewModule,
  ExpandMore,
  ExpandLess,
  Star,
  DeleteOutline,
} from '@mui/icons-material';
import axiosInstance from "../../api/axios-config";
import { useAuth } from "../../contexts/auth-context";
import { canModifyResource, UserRole } from "../../utils/role-permissions";
import { generateSimplePassword } from "../../utils/password-generator";
import { useNavigate } from "react-router-dom";

export interface IUser {
  id: number;
  email: string;
  gender?: string;
  fullname: string;
  phone?: string;
  hasBeenOnboarded: boolean;
  profilePicture?: string;
  createdAt?: string;
  role?: string;
  subGroupId?: number;
  subGroup?: {
    id: number;
    name: string;
  };
}

interface ISubGroupFilter {
  id: number;
  name: string;
  _count?: {
    users: number;
  };
}

/**
 * Maps backend error messages to translation keys for invite errors
 */
const getInviteErrorKey = (errorMessage: string | undefined): string => {
  if (!errorMessage) return "users.invite.errors.unknown";

  const errorMappings: Record<string, string> = {
    "User already exists and is assigned to another company": "users.invite.errors.user_already_in_company",
    "User limit exceeded": "users.invite.errors.user_limit_exceeded",
    "Limit user exceeded for this company": "users.invite.errors.user_limit_exceeded",
    "Email already exists": "users.invite.errors.email_already_exists",
    "Invalid email": "users.invite.errors.invalid_email",
    "Company not found": "users.invite.errors.company_not_found",
  };

  // Check for exact match first
  if (errorMappings[errorMessage]) {
    return errorMappings[errorMessage];
  }

  // Check for partial match (case-insensitive)
  const lowerMessage = errorMessage.toLowerCase();
  for (const [key, value] of Object.entries(errorMappings)) {
    if (lowerMessage.includes(key.toLowerCase())) {
      return value;
    }
  }

  return "users.invite.errors.unknown";
};

/**
 * Maps backend error messages to translation keys for CSV import errors
 */
const getImportErrorKey = (errorMessage: string | undefined): string => {
  if (!errorMessage) return "users.import.errors.unknown";

  const errorMappings: Record<string, string> = {
    "User already exists and is assigned to another company": "users.import.errors.user_already_in_company",
    "User limit exceeded": "users.import.errors.user_limit_exceeded",
    "Limit user exceeded for this company": "users.import.errors.user_limit_exceeded",
    "Invalid CSV": "users.import.errors.invalid_csv",
    "Missing columns": "users.import.errors.missing_columns",
  };

  // Check for exact match first
  if (errorMappings[errorMessage]) {
    return errorMappings[errorMessage];
  }

  // Check for partial match (case-insensitive)
  const lowerMessage = errorMessage.toLowerCase();
  for (const [key, value] of Object.entries(errorMappings)) {
    if (lowerMessage.includes(key.toLowerCase())) {
      return value;
    }
  }

  return "users.import.errors.unknown";
};

export const UserList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const { open } = useNotification();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const isMedium = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const isLarge = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
  const isXLarge = useMediaQuery(theme.breakpoints.down('xl'), { noSsr: true });
  const navigate = useNavigate();

  // Create responsive key for forcing re-renders
  const responsiveKey = `${isMobile}-${isMedium}-${isLarge}-${isXLarge}`;

  // Check if user can modify resources
  const canModify = currentUser?.role ? canModifyResource(currentUser.role as UserRole, 'users') : false;
  const isVigilane = currentUser?.role === 'VIGILANE';

  // Check if user is GUEST without company
  const isGuestWithoutCompany = currentUser?.role === 'GUEST' && !currentUser?.entrepriseId;

  // ADMIN et SAFEE_STAFF ont une vue globale sans sous-groupes
  const hasGlobalView = currentUser?.role === 'ADMIN' || currentUser?.role === 'SAFEE_STAFF';

  // OPERATOR users don't use sub-groups (they manage users via UserMonitoring, not enterprise hierarchy)
  const isOperator = currentUser?.role === 'OPERATOR';

  const [isLoading, setIsLoading] = useState(true);
  const [enterpriseUsers, setEnterpriseUsers] = useState<IUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [tabValue, setTabValue] = useState(0);

  // Sub-group filter state
  const [subGroups, setSubGroups] = useState<ISubGroupFilter[]>([]);
  const [selectedSubGroup, setSelectedSubGroup] = useState<ISubGroupFilter | null>(null);
  const [loadingSubGroups, setLoadingSubGroups] = useState(false);

  // View mode state (list vs grouped by sub-group) - default to grouped if sub-groups exist
  const [viewMode, setViewMode] = useState<'list' | 'grouped'>('grouped');
  const [expandedGroups, setExpandedGroups] = useState<Set<number | 'unassigned'>>(new Set());

  // OPERATOR invite dialog state
  const [operatorInviteDialog, setOperatorInviteDialog] = useState(false);
  const [operatorInviteFirstName, setOperatorInviteFirstName] = useState("");
  const [operatorInviteLastName, setOperatorInviteLastName] = useState("");
  const [operatorInviteEmail, setOperatorInviteEmail] = useState("");
  const [operatorInviteLoading, setOperatorInviteLoading] = useState(false);

  // OPERATOR unlink state
  const [operatorUnlinkLoading, setOperatorUnlinkLoading] = useState<number | null>(null);

  // État pour l'invitation rapide
  const [quickInviteDialog, setQuickInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteSubGroup, setInviteSubGroup] = useState<ISubGroupFilter | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  // État pour l'import CSV
  const [csvDialog, setCsvDialog] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Company usage/limit state
  const [companyLimit, setCompanyLimit] = useState<number | null>(null);
  const [companyUserCount, setCompanyUserCount] = useState<number | null>(null);
  const isFreeTrialModerator = currentUser?.role === 'MODERATOR_FREE';
  const effectiveLimit = isFreeTrialModerator ? 10 : (companyLimit ?? null);
  const usedSeats = companyUserCount ?? null;
  // QR invite dialog state
  const [qrDialog, setQrDialog] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [companies, setCompanies] = useState<Array<{ id: number; nom: string }>>([]);
  // Invite method picker dialog
  const [inviteMethodDialog, setInviteMethodDialog] = useState(false);

  // Stats
  const totalUsers = enterpriseUsers.length;
  const onboardedUsers = enterpriseUsers.filter(u => u.hasBeenOnboarded).length;
  const pendingUsers = totalUsers - onboardedUsers;

  // Helper to check if user is a SUB_MODERATOR of their sub-group
  const isSubModerator = (user: IUser) => user.role === 'SUB_MODERATOR';

  // Group users by sub-group for grouped view
  const usersGroupedBySubGroup = React.useMemo(() => {
    const groups: Map<number | 'unassigned', { name: string; users: IUser[]; moderators: IUser[] }> = new Map();

    // Initialize with "unassigned" group
    groups.set('unassigned', { name: translate("users.subGroup.unassigned", "Non assigné"), users: [], moderators: [] });

    // Initialize with existing sub-groups
    subGroups.forEach(sg => {
      groups.set(sg.id, { name: sg.name, users: [], moderators: [] });
    });

    // Distribute filtered users into groups, separating moderators
    filteredUsers.forEach(user => {
      const targetGroupId = user.subGroupId && groups.has(user.subGroupId)
        ? user.subGroupId
        : user.subGroup && groups.has(user.subGroup.id)
          ? user.subGroup.id
          : user.subGroup
            ? (() => {
                // Sub-group exists but wasn't in our list - create it
                if (!groups.has(user.subGroup.id)) {
                  groups.set(user.subGroup.id, { name: user.subGroup.name, users: [], moderators: [] });
                }
                return user.subGroup.id;
              })()
            : 'unassigned';

      const group = groups.get(targetGroupId)!;

      // SUB_MODERATOR users go to moderators array, others to users array
      if (isSubModerator(user) && targetGroupId !== 'unassigned') {
        group.moderators.push(user);
      } else {
        group.users.push(user);
      }
    });

    // Convert to array and sort: groups with users first, then alphabetically
    return Array.from(groups.entries())
      .map(([id, data]) => ({
        id,
        ...data,
        // Sort moderators alphabetically, then users alphabetically
        moderators: data.moderators.sort((a, b) => (a.fullname || '').localeCompare(b.fullname || '')),
        users: data.users.sort((a, b) => (a.fullname || '').localeCompare(b.fullname || '')),
        totalCount: data.moderators.length + data.users.length,
      }))
      .sort((a, b) => {
        // Groups with users come first
        if (a.totalCount > 0 && b.totalCount === 0) return -1;
        if (a.totalCount === 0 && b.totalCount > 0) return 1;
        // "Unassigned" at the end among non-empty groups
        if (a.id === 'unassigned') return 1;
        if (b.id === 'unassigned') return -1;
        // Alphabetically
        return a.name.localeCompare(b.name);
      });
  }, [filteredUsers, subGroups, translate]);

  // Toggle group expansion
  const toggleGroupExpansion = (groupId: number | 'unassigned') => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Expand/collapse all groups
  const toggleAllGroups = () => {
    const nonEmptyGroups = usersGroupedBySubGroup.filter(g => g.totalCount > 0);
    if (expandedGroups.size === nonEmptyGroups.length) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(nonEmptyGroups.map(g => g.id)));
    }
  };


  const fetchEnterpriseUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      let users: IUser[] = [];

      if (isOperator) {
        // OPERATOR: fetch managed users via dedicated endpoint (scoped by enterprise)
        const response = await axiosInstance.get('/monitoring/operators/managed-users');
        if (response.data && Array.isArray(response.data)) {
          users = response.data;
        }
      } else {
        // Request all users with a high limit since this component needs all data for:
        // - Stats calculation (total, onboarded, pending)
        // - Client-side filtering and grouping by sub-group
        const response = await axiosInstance.get('/users?limit=10000');

        // Handle both array response (legacy) and paginated response {data: [...], total: ...}
        if (response.data && Array.isArray(response.data)) {
          users = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          users = response.data.data;
        }
      }

      // Ne plus filtrer l'utilisateur actuel pour unifier le comptage avec CompanyShow
      setEnterpriseUsers(users);
      setFilteredUsers(users);
    } catch (error) {
      console.error("Failed to fetch enterprise users:", error);
      setEnterpriseUsers([]);
      setFilteredUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [isOperator]);

  useEffect(() => {
    // Don't fetch users if GUEST without company
    if (!isGuestWithoutCompany) {
      fetchEnterpriseUsers();
    } else {
      setIsLoading(false);
      setEnterpriseUsers([]);
      setFilteredUsers([]);
    }
  }, [currentUser, isGuestWithoutCompany, fetchEnterpriseUsers]);

  // Fetch company usage and limit (for seat/limit UI)
  useEffect(() => {
    const fetchCompanyUsage = async () => {
      try {
        const entrepriseId = currentUser?.entrepriseId;
        if (!entrepriseId) return;
        const { data } = await axiosInstance.get(`/company/count/${entrepriseId}`);
        // data: { company: { limitUser: number, ... }, count: number }
        if (data) {
          setCompanyLimit(typeof data.company?.limitUser === 'number' ? data.company.limitUser : null);
          setCompanyUserCount(typeof data.count === 'number' ? data.count : null);
        }
      } catch (e) {
        console.error('Failed to fetch company usage:', e);
      }
    };

    if (!isGuestWithoutCompany && !isOperator) {
      fetchCompanyUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.entrepriseId, isOperator]);

  // Fetch sub-groups for filtering (skip for ADMIN/SAFEE_STAFF who have global view)
  useEffect(() => {
    const fetchSubGroups = async () => {
      if (!currentUser?.entrepriseId || isGuestWithoutCompany || hasGlobalView || isOperator) return;

      setLoadingSubGroups(true);
      try {
        const response = await axiosInstance.get('/sub-groups');
        if (response.data && Array.isArray(response.data)) {
          setSubGroups(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch sub-groups:', error);
        setSubGroups([]);
      } finally {
        setLoadingSubGroups(false);
      }
    };

    fetchSubGroups();
  }, [currentUser?.entrepriseId, isGuestWithoutCompany, hasGlobalView, isOperator]);

  // Force list view for ADMIN/SAFEE_STAFF (global view) and OPERATOR
  useEffect(() => {
    if (hasGlobalView || isOperator) {
      setViewMode('list');
    }
  }, [hasGlobalView, isOperator]);

  useEffect(() => {
    let filtered = [...enterpriseUsers];

    // Filtrer par recherche
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
      );
    }

    // Filtrer par statut (tabs)
    if (tabValue === 1) {
      filtered = filtered.filter(user => user.hasBeenOnboarded);
    } else if (tabValue === 2) {
      filtered = filtered.filter(user => !user.hasBeenOnboarded);
    }

    // Filtrer par sous-groupe
    if (selectedSubGroup) {
      if (selectedSubGroup.id === -1) {
        // "Non assigné" - filter users without a sub-group
        filtered = filtered.filter(user => !user.subGroupId);
      } else {
        filtered = filtered.filter(user => user.subGroupId === selectedSubGroup.id);
      }
    }

    setFilteredUsers(filtered);
  }, [searchTerm, tabValue, enterpriseUsers, selectedSubGroup]);

  // Auto-update invite link when company selection changes in QR dialog
  useEffect(() => {
    if (!qrDialog) return;
    const cid = selectedCompanyId || currentUser?.entrepriseId;
    if (cid) {
      loadPersistentInviteLink(cid);
    }
  }, [qrDialog, selectedCompanyId, currentUser?.entrepriseId]);

  const handleQuickInvite = async () => {
    setInviteLoading(true);
    try {
      // Prevent inviting if limit reached
      if (effectiveLimit != null && usedSeats != null && usedSeats >= effectiveLimit) {
        open?.({
          type: "error",
          message: translate("users.limit.reached", "Limite atteinte"),
          description: translate(
            "users.limit.upgrade_desc",
            "Vous avez atteint la limite d'utilisateurs de votre abonnement. Mettez à niveau votre offre pour inviter plus d'utilisateurs."
          ),
        });
        return;
      }

      // Récupérer l'entreprise du current user si elle n'est pas déjà dans le contexte
      let entrepriseId = currentUser?.entrepriseId;

      // Si l'utilisateur est un MODERATOR sans entrepriseId dans le contexte,
      // on doit la récupérer depuis l'API
      if (!entrepriseId && currentUser?.role === 'MODERATOR') {
        try {
          const userResponse = await axiosInstance.get('/auth/me');
          entrepriseId = userResponse.data.entrepriseId;
        } catch {
          // Silently ignore - will use undefined entrepriseId
        }
      }

      const requestData: {
        email: string;
        fullname: string;
        password: string;
        entrepriseId: number | undefined;
        subGroupId?: number;
      } = {
        email: inviteEmail,
        fullname: inviteName,
        password: generateSimplePassword(), // Générer un mot de passe simple (6 caractères alphanumériques)
        entrepriseId: entrepriseId,
      };

      // Ajouter le sous-groupe si sélectionné
      if (inviteSubGroup && inviteSubGroup.id > 0) {
        requestData.subGroupId = inviteSubGroup.id;
      }

      await axiosInstance.post('/users/create-user', requestData);

      open?.({
        type: "success",
        message: translate("users.invite.success"),
        description: translate("users.invite.success_desc", { name: inviteName }),
      });

      // Réinitialiser et fermer
      setInviteEmail("");
      setInviteName("");
      setInviteSubGroup(null);
      setQuickInviteDialog(false);
      fetchEnterpriseUsers();
      // Refresh company usage after invite
      try {
        if (currentUser?.entrepriseId) {
          const { data } = await axiosInstance.get(`/company/count/${currentUser.entrepriseId}`);
          if (data) {
            setCompanyLimit(typeof data.company?.limitUser === 'number' ? data.company.limitUser : companyLimit);
            setCompanyUserCount(typeof data.count === 'number' ? data.count : companyUserCount);
          }
        }
      } catch {
        // Silently ignore refresh error
      }
    } catch (error: any) {
      const backendMessage = error.response?.data?.message;
      const errorKey = getInviteErrorKey(backendMessage);

      open?.({
        type: "error",
        message: translate("users.invite.error"),
        description: translate(errorKey),
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!file) return;

    // Prevent upload if limit reached
    if (effectiveLimit != null && usedSeats != null && usedSeats >= effectiveLimit) {
      open?.({
        type: "error",
        message: translate("users.limit.reached", "Limite atteinte"),
        description: translate(
          "users.limit.no_remaining",
          "Aucune invitation restante. Mettez à niveau votre offre pour importer de nouveaux utilisateurs."
        ),
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploadProgress(0);

    try {
      await axiosInstance.post('/users/csv-import', formData, {
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        },
      });

      open?.({
        type: "success",
        message: translate("users.import.success"),
        description: translate("users.import.success_desc"),
      });

      setCsvDialog(false);
      setFile(null);
      fetchEnterpriseUsers();
    } catch (error: any) {
      const backendMessage = error.response?.data?.message;
      const errorKey = getImportErrorKey(backendMessage);

      open?.({
        type: "error",
        message: translate("users.import.error"),
        description: translate(errorKey),
      });
    }
  };

  const downloadExampleCSV = () => {
    const csvContent = [
      "email,fullname,phone",
      "jean.dupont@exemple.com,Jean Dupont,+33612345678",
      "marie.martin@exemple.com,Marie Martin,+33698765432",
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'exemple_import_utilisateurs.csv');
    link.click();
  };

  const openQrDialog = async () => {
    setInviteLink(null);
    setSelectedCompanyId(currentUser?.entrepriseId || null);
    setQrDialog(true);
    // If no company id (e.g., ADMIN), fetch companies to choose from
    if (!currentUser?.entrepriseId) {
      try {
        const res = await axiosInstance.get('/company');
        if (Array.isArray(res.data)) {
          setCompanies(res.data);
          if (res.data.length === 1) {
            setSelectedCompanyId(res.data[0].id);
            loadPersistentInviteLink(res.data[0].id);
          }
        }
      } catch (e) {
        console.error('Failed to load companies', e);
      }
    } else {
      // Company known: auto-load persistent link immediately
      loadPersistentInviteLink(currentUser.entrepriseId);
    }
  };

  const loadPersistentInviteLink = (companyId: number) => {
    const origin = window.location.origin;
    const key = `inviteLink:${companyId}`;
    const stored = localStorage.getItem(key);
    const link = stored || `${origin}/invite/${companyId}`;
    if (!stored) {
      localStorage.setItem(key, link);
    }
    setInviteLink(link);
  };

  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      open?.({ type: 'success', message: translate("users.invite.qr.link_copied") });
    } catch {}
  };

  // OPERATOR invite handler
  const handleOperatorInvite = async () => {
    setOperatorInviteLoading(true);
    try {
      await axiosInstance.post('/monitoring/operators/invite-users', {
        invites: [{
          email: operatorInviteEmail,
          firstName: operatorInviteFirstName,
          lastName: operatorInviteLastName,
        }],
      });

      open?.({
        type: "success",
        message: translate("users.operator_invite.success", "Invitation envoyée"),
        description: translate("users.operator_invite.success_desc", "L'utilisateur a été invité avec succès"),
      });

      setOperatorInviteFirstName("");
      setOperatorInviteLastName("");
      setOperatorInviteEmail("");
      setOperatorInviteDialog(false);
      fetchEnterpriseUsers();
    } catch (error: any) {
      open?.({
        type: "error",
        message: translate("users.operator_invite.error", "Erreur lors de l'invitation"),
        description: error.response?.data?.message || translate("users.operator_invite.errors.unknown", "Une erreur inattendue s'est produite"),
      });
    } finally {
      setOperatorInviteLoading(false);
    }
  };

  // OPERATOR unlink handler
  const handleOperatorUnlink = useCallback(async (userEmail: string, userId: number) => {
    if (!window.confirm(translate("users.operator_unlink.confirm", "Êtes-vous sûr de vouloir retirer cet utilisateur ?"))) {
      return;
    }

    setOperatorUnlinkLoading(userId);
    try {
      await axiosInstance.delete('/monitoring/providers/OPERATOR/unlink-user', {
        data: { email: userEmail },
      });

      open?.({
        type: "success",
        message: translate("users.operator_unlink.success", "Utilisateur retiré avec succès"),
      });

      fetchEnterpriseUsers();
    } catch (error: any) {
      open?.({
        type: "error",
        message: translate("users.operator_unlink.error", "Erreur lors du retrait de l'utilisateur"),
        description: error.response?.data?.message,
      });
    } finally {
      setOperatorUnlinkLoading(null);
    }
  }, [translate, open, fetchEnterpriseUsers]);

  const columns = React.useMemo<GridColDef<IUser>[]>(
    () => [
      {
        field: "user",
        headerName: translate("users.fields.user"),
        flex: 1,
        minWidth: isMobile ? 200 : 300,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={isMobile ? 1 : 2} alignItems="center">
            <Avatar
              src={row.profilePicture}
              sx={{
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40,
                bgcolor: row.hasBeenOnboarded ? 'success.main' : 'grey.400'
              }}
            >
              {row.fullname?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {row.fullname}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.email}
              </Typography>
            </Box>
            {row.id === currentUser?.id && (
              <Chip
                label={translate("users.me", "Me")}
                size="small"
                color="primary"
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  height: { xs: '20px', sm: '24px' },
                  fontWeight: 600
                }}
              />
            )}
          </Stack>
        ),
      },
      {
        field: "phone",
        headerName: translate("users.fields.phone"),
        width: isMobile ? 120 : 150,
        renderCell: ({ value }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Phone fontSize="small" color="action" />
            <Typography variant="body2">{value || '-'}</Typography>
          </Stack>
        ),
      },
      {
        field: "hasBeenOnboarded",
        headerName: translate("users.fields.status"),
        width: isMobile ? 120 : 150,
        renderCell: ({ value }) => (
          <Chip
            label={value ? translate("users.status.active") : translate("users.status.pending")}
            color={value ? "success" : "warning"}
            size="small"
            icon={value ? <CheckCircle /> : <Cancel />}
          />
        ),
      },
      {
        field: "subGroup",
        headerName: translate("users.fields.subGroup", "Sous-groupe"),
        width: isMobile ? 130 : 160,
        renderCell: ({ row }) => (
          row.subGroup ? (
            <Chip
              label={row.subGroup.name}
              size="small"
              icon={<AccountTree fontSize="small" />}
              onClick={() => navigate(`/company`)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {translate("users.subGroup.unassigned", "Non assigné")}
            </Typography>
          )
        ),
      },
      {
        field: "actions",
        headerName: translate("common.actions"),
        width: isMobile ? 90 : (isOperator ? 100 : 120),
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={isMobile ? 0.5 : 1}>
            {canModify && !isVigilane && !isOperator && (
              <Tooltip title={translate("common.edit")}>
                <span>
                  <EditButton size={isMobile ? 'small' : 'medium'} hideText recordItemId={row.id} />
                </span>
              </Tooltip>
            )}
            {!isOperator && (
              <Tooltip title={translate("common.details")}>
                <span>
                  <ShowButton size={isMobile ? 'small' : 'medium'} hideText recordItemId={row.id} />
                </span>
              </Tooltip>
            )}
            {isOperator && (
              <Tooltip title={translate("users.operator_unlink.button", "Retirer")}>
                <span>
                  <IconButton
                    size={isMobile ? 'small' : 'medium'}
                    color="error"
                    onClick={() => handleOperatorUnlink(row.email, row.id)}
                    disabled={operatorUnlinkLoading === row.id}
                  >
                    {operatorUnlinkLoading === row.id ? <CircularProgress size={18} /> : <DeleteOutline />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canModify, translate, isMobile, isVigilane, isOperator, currentUser?.id, navigate, theme, operatorUnlinkLoading, handleOperatorUnlink]
  );

  // Pagination state for DataGrid
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: isMobile ? 10 : 25,
  });

  // Show special UI for GUEST users without company
  if (isGuestWithoutCompany) {
    return (
      <Grid container spacing={2} sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 0 } }}>
        <Grid item xs={12}>
          <Card
            sx={{
              maxWidth: 600,
              mx: 'auto',
              textAlign: 'center',
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${theme.palette.background.paper} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <CardContent sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 3
                }}
              >
                <Group sx={{ fontSize: 50, color: theme.palette.primary.main }} />
              </Box>

              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                {translate('users.no_access_title')}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                {translate('users.no_access_desc')}
              </Typography>

              <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto' }}>
                {translate('users.guest_info')}
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} key={responsiveKey}>
      {/* OPERATOR simplified header */}
      {isOperator && (
        <Grid item xs={12}>
          <Card
            sx={{
              mb: 3,
              maxWidth: "100%",
              overflow: "hidden",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.dark, 0.2)} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}>
                    {translate("users.operator.title", "Utilisateurs gérés")}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {translate("users.operator.subtitle", "Gérez les utilisateurs liés à votre service")}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip
                    label={`${totalUsers} ${translate("users.operator.userCount", "utilisateurs")}`}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={() => setOperatorInviteDialog(true)}
                    sx={{
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                      boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                    }}
                  >
                    {translate("users.actions.invite", "Inviter")}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Header avec stats */}
      {!isOperator && (
      <Grid item xs={12}>
        <Card
          sx={{
            mb: 3,
            maxWidth: "100%",
            overflow: "hidden",
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.primary.dark, 0.2)} 100%)`
                : `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.light,
                    0.1
                  )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 3 },
              "&:last-child": { pb: { xs: 2, sm: 3 } },
            }}
          >
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch">
              <Grid item xs={12} xl={8} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  gutterBottom
                  sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
                >
                  {isVigilane
                    ? translate("users.title_vigilane")
                    : translate("users.manage_team")}
                </Typography>
                {isVigilane ? (
                  <></>
                ) : (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {translate("users.manage_team_desc")}
                  </Typography>
                )}

                <Grid container spacing={2} sx={{ mt: 'auto' }}>
                  <Grid item xs={6} lg={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(
                          theme.palette.primary.main,
                          0.1
                        )}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="primary"
                        fontWeight={700}
                        sx={{
                          fontSize: { xs: "1.25rem", sm: "1.5rem", lg: "2rem" },
                        }}
                      >
                        {totalUsers}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      >
                        {translate("users.stats.total")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} lg={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        border: `1px solid ${alpha(
                          theme.palette.success.main,
                          0.2
                        )}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="success.main"
                        fontWeight={700}
                        sx={{
                          fontSize: { xs: "1.25rem", sm: "1.5rem", lg: "2rem" },
                        }}
                      >
                        {onboardedUsers}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      >
                        {translate("users.stats.active")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} lg={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        bgcolor: alpha(theme.palette.warning.main, 0.05),
                        border: `1px solid ${alpha(
                          theme.palette.warning.main,
                          0.2
                        )}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="warning.main"
                        fontWeight={700}
                        sx={{
                          fontSize: { xs: "1.25rem", sm: "1.5rem", lg: "2rem" },
                        }}
                      >
                        {pendingUsers}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      >
                        {translate("users.stats.pending")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} lg={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        bgcolor: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(
                          theme.palette.info.main,
                          0.1
                        )}`,
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="info.main"
                        fontWeight={700}
                        sx={{
                          fontSize: { xs: "1.25rem", sm: "1.5rem", lg: "2rem" },
                        }}
                      >
                        {Math.round((onboardedUsers / totalUsers) * 100) || 0}%
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      >
                        {translate("users.stats.activation_rate")}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} xl={4} sx={{ alignContent: "end" }}>
                {isVigilane ? (
                  <Stack spacing={2}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PersonAdd />}
                      fullWidth
                      onClick={() => navigate("/vigilane-link")}
                      sx={{
                        height: { xs: "60px", sm: "80px", md: "100px" },
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                        boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                      }}
                    >
                      {translate("users.actions.add")}
                    </Button>
                  </Stack>
                ) : canModify ? (
                  <Stack spacing={2}>
                    {/* Seats usage overview */}
                    {effectiveLimit != null && usedSeats != null && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.15
                          )}`,
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="subtitle2">
                              {translate("users.limit.title")}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${usedSeats}/${effectiveLimit}`}
                              color={
                                usedSeats >= effectiveLimit
                                  ? "error"
                                  : usedSeats / effectiveLimit >= 0.8
                                  ? "warning"
                                  : "default"
                              }
                            />
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={
                              effectiveLimit > 0
                                ? Math.min(
                                    100,
                                    Math.round(
                                      (usedSeats / effectiveLimit) * 100
                                    )
                                  )
                                : 0
                            }
                          />
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {translate("users.limit.remaining")}:{" "}
                              {Math.max(0, effectiveLimit - usedSeats)}
                            </Typography>
                            {isFreeTrialModerator && (
                              <Chip
                                size="small"
                                color="info"
                                label={translate(
                                  "users.limit.free_trial",
                                  "Essai gratuit"
                                )}
                              />
                            )}
                          </Stack>
                          {/* Alerts remplacées par des indicateurs compacts pour alléger l'UI */}
                        </Stack>
                      </Paper>
                    )}
                    {/* Bouton principal unique: ouvre une modale avec 3 choix */}
                    <Tooltip
                      title={
                        effectiveLimit != null &&
                        usedSeats != null &&
                        usedSeats >= effectiveLimit
                          ? translate(
                              "users.limit.reached_cta",
                              "Limite atteinte — mettez à niveau pour inviter"
                            )
                          : ""
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<PersonAdd />}
                          fullWidth
                          onClick={() => setInviteMethodDialog(true)}
                          disabled={
                            effectiveLimit != null &&
                            usedSeats != null &&
                            usedSeats >= effectiveLimit
                          }
                          sx={{
                            py: 1.5,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                            boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                            height: '97px'
                          }}
                        >
                          {translate("users.actions.invite", "Invite")}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      {translate("users.guest_readonly")}
                    </Typography>
                  </Alert>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      )}

      {/* Barre de recherche et filtres */}
      <Grid item xs={12}>
        <Card
          sx={{
            mb: 0,
            maxWidth: "100%",
            overflow: "hidden",
            borderBottomLeftRadius: "0 !important",
            borderBottomRightRadius: "0 !important",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={isOperator ? translate("users.operator.search", "Rechercher") : translate("users.search.placeholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {isOperator && (
                    <>
                      <IconButton
                        color="primary"
                        sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2) } }}
                      >
                        <Search />
                      </IconButton>
                      <IconButton
                        onClick={() => setSearchTerm("")}
                        disabled={!searchTerm}
                        sx={{ bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1), '&:hover': { bgcolor: (theme) => alpha(theme.palette.grey[500], 0.2) } }}
                      >
                        <Clear />
                      </IconButton>
                    </>
                  )}
                </Stack>
              </Grid>

              {/* Sub-group filter (hidden for ADMIN/SAFEE_STAFF with global view and OPERATOR) */}
              {subGroups.length > 0 && !hasGlobalView && !isOperator && (
                <Grid item xs={12} sm={6} md="auto">
                  <Autocomplete
                    size={isMobile ? "small" : "medium"}
                    options={[
                      { id: -1, name: translate("users.subGroup.unassigned", "Non assigné"), _count: { users: enterpriseUsers.filter(u => !u.subGroupId).length } } as ISubGroupFilter,
                      ...subGroups,
                    ]}
                    getOptionLabel={(option) => option.name}
                    value={selectedSubGroup}
                    onChange={(_, newValue) => setSelectedSubGroup(newValue)}
                    loading={loadingSubGroups}
                    sx={{ minWidth: { xs: '100%', sm: 200, md: 220 } }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={translate("users.filter.subGroup", "Sous-groupe")}
                        placeholder={translate("users.filter.allSubGroups", "Tous")}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <FilterList fontSize="small" color="action" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {loadingSubGroups ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li key={key} {...optionProps}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                            <Stack direction="row" alignItems="center" gap={1}>
                              <AccountTree fontSize="small" color={option.id === -1 ? "disabled" : "action"} />
                              <Typography variant="body2" color={option.id === -1 ? "text.secondary" : "text.primary"}>
                                {option.name}
                              </Typography>
                            </Stack>
                            {option._count?.users !== undefined && (
                              <Chip
                                label={option._count.users}
                                size="small"
                                sx={{ ml: 1, minWidth: 28, height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Stack>
                        </li>
                      );
                    }}
                  />
                </Grid>
              )}

              {/* Clear filter button (hidden for ADMIN/SAFEE_STAFF with global view and OPERATOR) */}
              {selectedSubGroup && !hasGlobalView && !isOperator && (
                <Grid item xs="auto">
                  <Tooltip title={translate("users.filter.clearSubGroup", "Effacer le filtre")}>
                    <IconButton
                      size="small"
                      onClick={() => setSelectedSubGroup(null)}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}

              {!isOperator && (
              <Grid item xs={12} md="auto">
                <Tabs
                  value={tabValue}
                  onChange={(_, newValue) => setTabValue(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    "& .MuiTab-root": {
                      minWidth: { xs: "auto", sm: 120 },
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    },
                  }}
                >
                  <Tab
                    label={translate("users.tabs.all", { count: totalUsers })}
                  />
                  <Tab
                    label={translate("users.tabs.active", {
                      count: onboardedUsers,
                    })}
                  />
                  <Tab
                    label={translate("users.tabs.pending", {
                      count: pendingUsers,
                    })}
                  />
                </Tabs>
              </Grid>
              )}

              {/* View mode toggle (hidden for ADMIN/SAFEE_STAFF with global view and OPERATOR) */}
              {subGroups.length > 0 && !hasGlobalView && !isOperator && (
                <Grid item xs="auto">
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, newMode) => newMode && setViewMode(newMode)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: { xs: 1, sm: 1.5 },
                      },
                    }}
                  >
                    <ToggleButton value="list">
                      <Tooltip title={translate("users.view.list", "Vue liste")}>
                        <ViewList fontSize="small" />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="grouped">
                      <Tooltip title={translate("users.view.grouped", "Vue par sous-groupe")}>
                        <ViewModule fontSize="small" />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Liste des utilisateurs */}
      <Grid item xs={12} sx={{ paddingTop: "0 !important" }}>
        <Card
          sx={{
            maxWidth: "100%",
            overflow: "hidden",
            borderTopLeftRadius: "0 !important",
            borderTopRightRadius: "0 !important",
          }}
        >
          <List canCreate={currentUser?.role === "ADMIN" || currentUser?.role === "SAFEE_STAFF"}>
            {isLoading ? (
              <Box
                sx={{
                  height: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : viewMode === 'list' ? (
              <Box sx={{ width: "100%", overflow: "auto" }}>
                <DataGrid
                  rows={filteredUsers}
                  columns={columns}
                  autoHeight
                  density={isMobile ? "compact" : "standard"}
                  rowHeight={isMobile ? 56 : 70}
                  disableRowSelectionOnClick
                  pageSizeOptions={isMobile ? [5, 10, 25] : [10, 25, 50, 100]}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  columnVisibilityModel={{
                    phone: !isMobile,
                    hasBeenOnboarded: !isMobile,
                    subGroup: !isMobile && subGroups.length > 0 && !hasGlobalView && !isOperator,
                  }}
                  disableColumnMenu
                  sx={{
                    minWidth: { xs: "100%", sm: "auto" },
                    "& .MuiDataGrid-cell": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiDataGrid-row": {
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      minWidth: "100%",
                    },
                  }}
                />
              </Box>
            ) : (
              /* Vue groupée par sous-groupe */
              <Box>
                {/* Bouton pour déplier/replier tous les groupes */}
                <Stack direction="row" justifyContent="flex-end" sx={{ px: 2, py: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={toggleAllGroups}
                    startIcon={expandedGroups.size === usersGroupedBySubGroup.filter(g => g.totalCount > 0).length ? <ExpandLess /> : <ExpandMore />}
                  >
                    {expandedGroups.size === usersGroupedBySubGroup.filter(g => g.totalCount > 0).length
                      ? translate("users.view.collapseAll", "Tout replier")
                      : translate("users.view.expandAll", "Tout déplier")}
                  </Button>
                </Stack>

                <Stack spacing={1.5} sx={{ px: 2, pb: 2 }}>
                  {usersGroupedBySubGroup.map((group) => (
                    <Paper
                      key={group.id}
                      elevation={0}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        opacity: group.totalCount === 0 ? 0.5 : 1,
                      }}
                    >
                      {/* En-tête du groupe */}
                      <Box
                        onClick={() => group.totalCount > 0 && toggleGroupExpansion(group.id)}
                        sx={{
                          px: 2,
                          py: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: group.totalCount > 0 ? 'pointer' : 'default',
                          bgcolor: alpha(
                            group.id === 'unassigned'
                              ? theme.palette.grey[500]
                              : theme.palette.primary.main,
                            0.03
                          ),
                          '&:hover': group.totalCount > 0 ? {
                            bgcolor: alpha(
                              group.id === 'unassigned'
                                ? theme.palette.grey[500]
                                : theme.palette.primary.main,
                              0.08
                            ),
                          } : {},
                          transition: 'background-color 0.2s',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <AccountTree
                            fontSize="small"
                            color={group.id === 'unassigned' ? 'disabled' : 'primary'}
                          />
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            color={group.id === 'unassigned' ? 'text.secondary' : 'text.primary'}
                          >
                            {group.name}
                          </Typography>
                          <Chip
                            label={group.totalCount}
                            size="small"
                            color={group.totalCount > 0 ? 'primary' : 'default'}
                            sx={{
                              height: 24,
                              minWidth: 32,
                              fontWeight: 600,
                            }}
                          />
                          {/* Badge modérateurs */}
                          {group.moderators.length > 0 && (
                            <Chip
                              icon={<Star sx={{ fontSize: 14 }} />}
                              label={`${group.moderators.length} ${translate("users.subGroup.moderator", "responsable")}${group.moderators.length > 1 ? 's' : ''}`}
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </Stack>
                        {group.totalCount > 0 && (
                          <IconButton size="small">
                            {expandedGroups.has(group.id) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </Box>

                      {/* Liste des utilisateurs du groupe */}
                      <Collapse in={expandedGroups.has(group.id)}>
                        <Divider />

                        {/* Section modérateurs (en haut avec mise en avant) */}
                        {group.moderators.length > 0 && (
                          <>
                            <Box
                              sx={{
                                px: 2,
                                py: 0.75,
                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                                <Typography variant="caption" fontWeight={600} color="warning.dark">
                                  {translate("users.subGroup.moderators", "Responsables du groupe")}
                                </Typography>
                              </Stack>
                            </Box>
                            <Stack
                              divider={<Divider flexItem />}
                              sx={{
                                bgcolor: alpha(theme.palette.warning.main, 0.03),
                              }}
                            >
                              {group.moderators.map((user) => (
                                <Box
                                  key={user.id}
                                  sx={{
                                    px: 2,
                                    py: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.warning.main, 0.08),
                                    },
                                  }}
                                >
                                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ position: 'relative' }}>
                                      <Avatar
                                        src={user.profilePicture}
                                        sx={{
                                          width: isMobile ? 36 : 44,
                                          height: isMobile ? 36 : 44,
                                          bgcolor: 'warning.main',
                                          border: `2px solid ${theme.palette.warning.main}`,
                                        }}
                                      >
                                        {user.fullname?.[0]?.toUpperCase()}
                                      </Avatar>
                                      <Star
                                        sx={{
                                          position: 'absolute',
                                          bottom: -2,
                                          right: -2,
                                          fontSize: 16,
                                          color: 'warning.main',
                                          bgcolor: 'background.paper',
                                          borderRadius: '50%',
                                        }}
                                      />
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="body1" fontWeight={600} noWrap>
                                          {user.fullname}
                                        </Typography>
                                        <Chip
                                          icon={<Star sx={{ fontSize: 12 }} />}
                                          label={translate("users.subGroup.moderator", "Responsable")}
                                          size="small"
                                          color="warning"
                                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                        />
                                        {user.id === currentUser?.id && (
                                          <Chip
                                            label={translate("users.me", "Me")}
                                            size="small"
                                            color="primary"
                                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                          />
                                        )}
                                      </Stack>
                                      <Typography variant="body2" color="text.secondary" noWrap>
                                        {user.email}
                                      </Typography>
                                      {!isMobile && user.phone && (
                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                          <Phone fontSize="small" sx={{ fontSize: 14, color: 'text.disabled' }} />
                                          <Typography variant="caption" color="text.secondary">
                                            {user.phone}
                                          </Typography>
                                        </Stack>
                                      )}
                                    </Box>
                                  </Stack>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                      label={user.hasBeenOnboarded ? translate("users.status.active") : translate("users.status.pending")}
                                      color={user.hasBeenOnboarded ? "success" : "warning"}
                                      size="small"
                                      icon={user.hasBeenOnboarded ? <CheckCircle /> : <Cancel />}
                                      sx={{ display: { xs: 'none', sm: 'flex' } }}
                                    />
                                    <Stack direction="row" spacing={0.5}>
                                      {canModify && !isVigilane && (
                                        <Tooltip title={translate("common.edit")}>
                                          <span>
                                            <EditButton size="small" hideText recordItemId={user.id} />
                                          </span>
                                        </Tooltip>
                                      )}
                                      <Tooltip title={translate("common.details")}>
                                        <span>
                                          <ShowButton size="small" hideText recordItemId={user.id} />
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                            {group.users.length > 0 && (
                              <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.5) }} />
                            )}
                          </>
                        )}

                        {/* Section membres réguliers */}
                        {group.users.length > 0 && (
                          <Stack divider={<Divider flexItem />}>
                            {group.users.map((user) => (
                              <Box
                                key={user.id}
                                sx={{
                                  px: 2,
                                  py: 1.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                                  },
                                }}
                              >
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                  <Avatar
                                    src={user.profilePicture}
                                    sx={{
                                      width: isMobile ? 36 : 44,
                                      height: isMobile ? 36 : 44,
                                      bgcolor: user.hasBeenOnboarded ? 'success.main' : 'grey.400',
                                    }}
                                  >
                                    {user.fullname?.[0]?.toUpperCase()}
                                  </Avatar>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Typography variant="body1" fontWeight={600} noWrap>
                                        {user.fullname}
                                      </Typography>
                                      {user.id === currentUser?.id && (
                                        <Chip
                                          label={translate("users.me", "Me")}
                                          size="small"
                                          color="primary"
                                          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                        />
                                      )}
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      {user.email}
                                    </Typography>
                                    {!isMobile && user.phone && (
                                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                        <Phone fontSize="small" sx={{ fontSize: 14, color: 'text.disabled' }} />
                                        <Typography variant="caption" color="text.secondary">
                                          {user.phone}
                                        </Typography>
                                      </Stack>
                                    )}
                                  </Box>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Chip
                                    label={user.hasBeenOnboarded ? translate("users.status.active") : translate("users.status.pending")}
                                    color={user.hasBeenOnboarded ? "success" : "warning"}
                                    size="small"
                                    icon={user.hasBeenOnboarded ? <CheckCircle /> : <Cancel />}
                                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                                  />
                                  <Stack direction="row" spacing={0.5}>
                                    {canModify && !isVigilane && (
                                      <Tooltip title={translate("common.edit")}>
                                        <span>
                                          <EditButton size="small" hideText recordItemId={user.id} />
                                        </span>
                                      </Tooltip>
                                    )}
                                    <Tooltip title={translate("common.details")}>
                                      <span>
                                        <ShowButton size="small" hideText recordItemId={user.id} />
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Collapse>
                    </Paper>
                  ))}
                </Stack>

                {/* Message si aucun utilisateur */}
                {filteredUsers.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {translate("users.noResults", "Aucun utilisateur trouvé")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </List>
        </Card>
      </Grid>

      {/* Dialog: choix de la méthode d'invitation */}
      <Dialog
        open={inviteMethodDialog}
        onClose={() => setInviteMethodDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              {translate(
                "users.invite.choose_method",
                "Choisir une méthode d'invitation"
              )}
            </Typography>
            <IconButton
              onClick={() => setInviteMethodDialog(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonAdd />}
                onClick={() => {
                  setInviteMethodDialog(false);
                  setQuickInviteDialog(true);
                }}
                disabled={
                  effectiveLimit != null &&
                  usedSeats != null &&
                  usedSeats >= effectiveLimit
                }
              >
                {translate("users.invite.manual", "Manuel")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GroupAdd />}
                onClick={() => {
                  setInviteMethodDialog(false);
                  openQrDialog();
                }}
                disabled={
                  effectiveLimit != null &&
                  usedSeats != null &&
                  usedSeats >= effectiveLimit
                }
              >
                {translate("users.invite.qr.title")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => {
                  setInviteMethodDialog(false);
                  setCsvDialog(true);
                }}
                disabled={
                  effectiveLimit != null &&
                  usedSeats != null &&
                  usedSeats >= effectiveLimit
                }
              >
                {translate("users.invite.csv", "Import CSV")}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {effectiveLimit != null && usedSeats != null
              ? translate("users.limit.remaining_inline", {
                  count: Math.max(0, effectiveLimit - usedSeats),
                })
              : ""}
          </Typography>
          <Button onClick={() => setInviteMethodDialog(false)}>
            {translate("common.cancel", "Annuler")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'invitation rapide */}
      <Dialog
        open={quickInviteDialog}
        onClose={() => setQuickInviteDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              {translate("users.invite.title")}
            </Typography>
            <IconButton
              onClick={() => setQuickInviteDialog(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Alert
            severity="info"
            sx={{
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            {translate("users.invite.info")}
            {effectiveLimit != null && usedSeats != null && (
              <>
                <br />
                {translate("users.limit.remaining_inline", {
                  count: Math.max(0, effectiveLimit - usedSeats),
                })}
              </>
            )}
          </Alert>

          <Stack spacing={3}>
            <TextField
              fullWidth
              label={translate("users.fields.fullname")}
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder={translate("users.fields.fullname_placeholder")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonAdd />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label={translate("users.fields.email")}
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={translate("users.fields.email_placeholder")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            {/* Sélecteur de sous-groupe (optionnel) */}
            {subGroups.length > 0 && (
              <Autocomplete
                options={subGroups}
                getOptionLabel={(option) => option.name}
                value={inviteSubGroup}
                onChange={(_, newValue) => setInviteSubGroup(newValue)}
                loading={loadingSubGroups}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate("users.invite.subGroup", "Sous-groupe (optionnel)")}
                    placeholder={translate("users.invite.subGroupPlaceholder", "Sélectionner un sous-groupe...")}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <AccountTree />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingSubGroups ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <AccountTree fontSize="small" color="action" />
                        <Typography variant="body2">{option.name}</Typography>
                        {option._count?.users !== undefined && (
                          <Chip
                            label={`${option._count.users} ${translate("users.invite.members", "membres")}`}
                            size="small"
                            sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Stack>
                    </li>
                  );
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setQuickInviteDialog(false)}>
            {translate("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleQuickInvite}
            disabled={
              !inviteEmail ||
              !inviteName ||
              inviteLoading ||
              (effectiveLimit != null &&
                usedSeats != null &&
                usedSeats >= effectiveLimit)
            }
            startIcon={
              inviteLoading ? <CircularProgress size={20} /> : <Send />
            }
          >
            {translate("users.invite.submit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog QR Code Invitation */}
      <Dialog
        open={qrDialog}
        onClose={() => setQrDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              {translate("users.invite.qr.title")}
            </Typography>
            <IconButton onClick={() => setQrDialog(false)} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={{ xs: 1.5, sm: 2 }}>
            {!currentUser?.entrepriseId && (
              <TextField
                select
                fullWidth
                label={translate("users.invite.qr.company")}
                value={selectedCompanyId || ""}
                onChange={(e: any) =>
                  setSelectedCompanyId(Number(e.target.value))
                }
                SelectProps={{ native: true }}
              >
                <option value="" disabled>
                  {translate("users.invite.qr.selectCompany")}
                </option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                  </option>
                ))}
              </TextField>
            )}
            <Alert severity="info">{translate("users.invite.qr.info")}</Alert>
            {inviteLink ? (
              <Stack spacing={2} alignItems="center">
                <Box
                  sx={{
                    p: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <img
                    alt="QR"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                      inviteLink
                    )}`}
                    style={{
                      width: isMobile ? 180 : 200,
                      height: "auto",
                      maxWidth: "100%",
                    }}
                  />
                </Box>
                <TextField
                  fullWidth
                  value={inviteLink}
                  label={translate("users.invite.qr.link_label")}
                  InputProps={{ readOnly: true }}
                />
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={copyInviteLink}
                    startIcon={<ContentCopy />}
                  >
                    {translate("users.invite.qr.copyLink")}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={() => {
                      const companyIdForLink =
                        selectedCompanyId ||
                        currentUser?.entrepriseId ||
                        "company";
                      const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=${encodeURIComponent(
                        inviteLink || ""
                      )}`;
                      const a = document.createElement("a");
                      a.href = qrSrc;
                      a.download = `safee-invite-${companyIdForLink}.png`;
                      a.target = "_blank";
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                  >
                    {translate("users.invite.qr.download")}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setQrDialog(false)}>
            {translate("users.invite.qr.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog invitation OPERATOR */}
      <Dialog
        open={operatorInviteDialog}
        onClose={() => setOperatorInviteDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
              {translate("users.operator_invite.title", "Inviter un utilisateur")}
            </Typography>
            <IconButton onClick={() => setOperatorInviteDialog(false)} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
            {translate("users.operator_invite.info", "Invitez un nouvel utilisateur à être suivi par votre service")}
          </Alert>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label={translate("users.operator_invite.firstName", "Prénom")}
              value={operatorInviteFirstName}
              onChange={(e) => setOperatorInviteFirstName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonAdd />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label={translate("users.operator_invite.lastName", "Nom")}
              value={operatorInviteLastName}
              onChange={(e) => setOperatorInviteLastName(e.target.value)}
            />
            <TextField
              fullWidth
              label={translate("users.fields.email")}
              type="email"
              value={operatorInviteEmail}
              onChange={(e) => setOperatorInviteEmail(e.target.value)}
              placeholder={translate("users.fields.email_placeholder")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOperatorInviteDialog(false)}>
            {translate("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleOperatorInvite}
            disabled={!operatorInviteEmail || !operatorInviteFirstName || !operatorInviteLastName || operatorInviteLoading}
            startIcon={operatorInviteLoading ? <CircularProgress size={20} /> : <Send />}
          >
            {translate("users.operator_invite.submit", "Inviter")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'import CSV */}
      <Dialog
        open={csvDialog}
        onClose={() => setCsvDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              {translate("users.import.title")}
            </Typography>
            <IconButton onClick={() => setCsvDialog(false)} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={{ xs: 2, sm: 3 }}>
            <Alert severity="info">
              {translate("users.import.info")}
              {effectiveLimit != null && usedSeats != null && (
                <>
                  <br />
                  {translate("users.import.remaining_note", {
                    count: Math.max(0, effectiveLimit - usedSeats),
                  })}
                </>
              )}
            </Alert>

            <Paper
              sx={{
                p: 4,
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                textAlign: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Upload sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />

              <input
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ display: "none" }}
                id="csv-upload"
              />

              <label htmlFor="csv-upload">
                <Button
                  component="span"
                  variant="contained"
                  size="large"
                  startIcon={<Upload />}
                >
                  {translate("users.import.choose_file")}
                </Button>
              </label>

              {file && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={file.name}
                    onDelete={() => setFile(null)}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {translate("users.import.uploading", {
                    progress: uploadProgress,
                  })}
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {translate("users.import.csv_format")}:
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor:
                    theme.palette.mode === "dark" ? "grey.900" : "grey.100",
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                }}
              >
                email,fullname,phone
                <br />
                jean.dupont@exemple.com,Jean Dupont,+33612345678
                <br />
                marie.martin@exemple.com,Marie Martin,+33698765432
              </Paper>
            </Box>

            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadExampleCSV}
              fullWidth
            >
              {translate("users.import.download_template")}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCsvDialog(false)}>
            {translate("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleFileUpload}
            disabled={
              !file ||
              (effectiveLimit != null &&
                usedSeats != null &&
                usedSeats >= effectiveLimit)
            }
            startIcon={<Upload />}
          >
            {translate("users.import.submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
